import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';

import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Select, SelectProps } from 'antd';
import { Modal as antModal } from 'antd';
import Pagination from 'component/pagination';
import SectionTitle from 'component/section-title/sectionTitle';
import { useDebounce } from 'tools/hooks/debounce.hook';
import { Button, Input, Label, Table, Typography } from 'ui';
import BackLink from 'ui/BackLink';

import {
  useLazyExportPromoterFollowersQuery,
  useLazyGetPromoterFollowersQuery,
} from 'services/events/eventsApiService';
import { PromoterFollower } from 'services/events/evetns.api.type';
import { useLazyGetCurrentUserQuery } from 'services/user-management/userManagementApiService';

import s from './PromoFans.module.scss';
import { FollowerFilterType, SortByFollowersEnum, initialFilter } from './promofans.utils';

const { confirm } = antModal;

const PromoFans: FC = () => {
  const [exportPromoterFollowers] = useLazyExportPromoterFollowersQuery();
  const [getCurrentUser, { data: currentUser }] = useLazyGetCurrentUserQuery();
  const [getPromoterFollowers, { data: followers, isFetching: followerFetching }] =
    useLazyGetPromoterFollowersQuery();
  const [requestFilter, setRequestFilter] = useState<FollowerFilterType>(initialFilter);
  const [filterEmail, setFilterEmail] = useState<string>('');
  const debouncedSearchEventName = useDebounce(filterEmail, 500) as string;
  const [tableData, setTableData] = useState<PromoterFollower[]>([]);
  const [isCanFetch, setIsCanFetch] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<SortByFollowersEnum>(SortByFollowersEnum.EMAIL);
  const [isDownLoading, setIsDownLoading] = useState<boolean>();
  const [promoterId, setPromoterId] = useState<string>();

  const showedElements =
    Number(followers?.followers?.length) +
    (Number(followers?.pageNumber) - 1) * Number(requestFilter.PageSize);

  const handlePageChange = useCallback((PageNumber: number) => {
    setRequestFilter((prevState) => ({
      ...prevState,
      PageNumber,
    }));
  }, []);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterEmail(event.target.value);
  };

  useEffect(() => {
    if (sortBy) {
      setRequestFilter((prevState) => ({
        ...prevState,
        SortBy: sortBy,
      }));
    }
  }, [sortBy]);

  const promoterOptions: SelectProps['options'] = currentUser?.promoters?.map((item) => ({
    label: item.name,
    value: item.promoterId,
  }));

  useEffect(() => {
    if (currentUser) {
      setPromoterId(currentUser?.promoters?.[0]?.promoterId);
    }
  }, [currentUser]);

  useEffect(() => {
    if (debouncedSearchEventName?.length >= 3) {
      setRequestFilter((prevState) => ({
        ...prevState,
        Term: debouncedSearchEventName,
      }));
    } else if (debouncedSearchEventName?.length === 0) {
      setRequestFilter((prevState) => ({
        ...prevState,
        Term: '',
      }));
    }
  }, [debouncedSearchEventName]);

  useEffect(() => {
    const temp = currentUser?.promoters.some((promoter) => promoter?.promoterId === promoterId);
    setIsCanFetch(temp!);
  }, [currentUser, promoterId]);

  useEffect(() => {
    if (promoterId && isCanFetch) {
      const updatedFilter = {
        ...requestFilter,
        Id: promoterId,
      };
      getPromoterFollowers(updatedFilter);
    }
  }, [requestFilter, promoterId, isCanFetch, getPromoterFollowers]);

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  const showExportConfirm = async () => {
    confirm({
      title: 'Would you like to export the followers of this promoter?',
      icon: <InfoCircleOutlined />,
      content: '',
      centered: true,
      mask: true,
      async onOk() {
        setIsDownLoading(true);
        if (promoterId) {
          exportPromoterFollowers({ id: promoterId }).then((data) => {
            if (data.data) {
              const link = document.createElement('a');
              link.href = data.data?.fileName;
              link.setAttribute('download', `Followers-${promoterId}.csv`);
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setIsDownLoading(false);
            }
          });
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleExport = () => {
    showExportConfirm();
  };

  useEffect(() => {
    setTableData(followers?.followers!);
  }, [setTableData, followers]);

  const columns = [
    {
      title: 'E-MAIL',
      dataIndex: 'email',
      key: 'email',
      render: (follower) => `${follower}`,
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (follower) => `${follower}`,
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      render: (follower) => `${follower}`,
    },
    {
      title: 'Number of Tickets',
      dataIndex: 'numberOfTickets',
      key: 'numberOfTickets',
      render: (follower) => `${follower}`,
    },
    {
      title: 'Mobile Number',
      dataIndex: 'mobileNumber',
      key: 'mobileNumber',
      render: (follower) => `${follower}`,
    },
    {
      title: 'Following Date',
      dataIndex: 'followingDate',
      key: 'followingDate',
      render: (follower) => `${follower}`,
    },
  ];

  return (
    <div className={s.wrapper}>
      <BackLink />
      <div className={s.row}>
        <SectionTitle label="common_promoter_followers" className={s.title} />
        <div className={s.titleButton}>
          <Button
            size="large"
            style={{ fontSize: 13 }}
            loading={isDownLoading}
            type="primary"
            onClick={handleExport}>
            <DownloadOutlined />
            EXPORT LIST
          </Button>
        </div>
      </div>
      <div className={s.content}>
        <div className={s.notificationList}>
          <div className={s.followerSearch}>
            <div className={s.filterWrapper}>
              <Input
                name="email"
                fullWidth
                placeholder="Name, E-mail or Mobile"
                value={filterEmail}
                onChange={handleSearchChange}
              />
              <Select
                className={s.sortBy}
                onChange={(value) => setSortBy(value)}
                options={[
                  { value: SortByFollowersEnum.NAME, label: 'Name' },
                  { value: SortByFollowersEnum.EMAIL, label: 'E-mail' },
                  { value: SortByFollowersEnum.NUMBER_OF_TICKETS, label: 'Number of tickets' },
                ]}
                defaultValue={sortBy}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 12,
                width: '100%',
              }}>
              <Typography type="label">Promoter:</Typography>
              <Select
                value={promoterId}
                className={s.promoterSelect}
                size="large"
                onChange={(value) => setPromoterId(value)}
                defaultValue={currentUser?.promoters?.[0]?.promoterId ?? ''}
                options={promoterOptions}
                placeholder="Choose promoter"
              />
            </div>
          </div>
          <Label>A list of all your promoter followers can be found underneath.</Label>
          <Table
            dataSource={tableData}
            loading={followerFetching}
            columns={columns}
            pagination={false}
            scroll={{ x: 768 }}
            rowKey="orderId"
          />
          {showedElements > 0 && (
            <Pagination
              totalElements={followers?.totalRecords}
              showedElements={showedElements}
              currentPage={requestFilter?.PageNumber || 0}
              total={followers?.totalPages}
              handlePageChange={handlePageChange}
              handleSizeChange={(size) =>
                setRequestFilter((prevValue) => ({ ...prevValue, PageSize: size }))
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoFans;
